<template>
  <div ref="monitoringCard" class="monitoring-card" :class="{...cardClasses, 'has-animated-nudge': isAnimatedNudge, 'without-content': withoutContent, 'without-settings': !hasSettings, 'auto-header-height': autoHeaderHeight }" :style="{ outlineColor: nudgeColor }">
    <div class="monitoring-card__header" :style="{ backgroundColor: nudgeColor }">
      <div class="division-color" v-if="hasSettings || divisionColor" :style="{ backgroundColor: widgetModel?.monitor?.division.color || divisionColor}" :title="widgetModel?.monitor?.division.name"></div>
      <card-badge v-if="showBadge && widgetModel && widgetModel.monitor && widgetModel.name != 'video' && widgetModel.settings.badge.enabled" :badge-data="widgetModel.settings.badge"></card-badge>
      <span v-if="hasSettings" class="monitoring-card__settings">
        <b-dropdown :ref="`dropdown-${widgetModel.id}`">
          <template #trigger>
            <i class="mdi mdi-dots-vertical"></i>
          </template>
          <!--
            Triggered when click delete button in the settings menu.
            @property {number} Widget Id
          -->
          <template v-if="!widgetModel.isGuest">
            <b-dropdown-item @click="$emit('delete-widget', widgetModel.id)">
              <i class="mdi mdi-trash-can-outline"></i>
              Remove
            </b-dropdown-item>
            <b-dropdown-item v-if="canChangeBlockType && $store.state.currentUser.enabled_flags.includes('patient_insights_dynamic_blocks')" @click="$emit('change-block-type', widgetModel.id, widgetModel.monitor.id)">
              <i class="mdi mdi-swap-vertical-variant"></i>
              Change Block Type
            </b-dropdown-item>
            <b-dropdown-item separator></b-dropdown-item>
          </template>
          <template v-if="componentSettings.length > 0">
            <div v-show="setting._isVisible" :key="index" v-for="(setting, index) in componentSettings">
              <b-dropdown-item
                @click="setting.handler ? setting.handler($event): ''" :separator="setting.separator"
                :custom="typeof setting.isClickable === 'undefined' ? false : !setting.isClickable"  :focusable="setting.isClickable"
                :class="[setting.parentContainerClass || '', {'is-section': setting.isSection}, setting.type]" class="settings-buttons">
                <b-field expanded v-if="setting.type == 'button-group'" class="has-addons">
                  <b-radio-button v-for="(btn, index) in setting.buttons" :key="index" v-model="setting.model"
                                  @input="setting.handler ? setting.handler($event) : '';$refs[`dropdown-${widgetModel.id}`].toggle()" :native-value="btn.value" size="is-small">
                    <span>{{ btn.title }}</span>
                  </b-radio-button>
                </b-field>
                <template v-else>
                  <i v-if="setting.icon" :class="setting.icon"></i>{{ setting.title }}
                  <template v-if="typeof setting.component == 'function'">
                    <component :is="setting.component()" v-bind="setting.componentAttributes"></component>
                  </template>
                </template>
              </b-dropdown-item>
            </div>
          </template>
        </b-dropdown>
      </span>
      <div class="monitoring-card__title-container">
        <div class="monitoring-card__title-tooltip" v-tooltip="{ content: titleTooltip, theme: 'lookdeep-global' }">
          <span class="monitoring-card__title">
            <template v-if="title">{{title}}</template>
            <template v-else-if="cardTitle">{{cardTitle}}</template>
            <!-- @slot Title slot if you want custom content in the header title -->
            <slot v-else-if="$slots.title" name="title"></slot>
            <template v-else-if="cSubtitle">{{ cSubtitle }}</template>
            <ld-skeleton v-else width="70%"></ld-skeleton>
            <router-link
              v-if="isWebrtcVideoWidget && canEditPatient && widgetModel?.monitor?.patient"
              class="monitoring-card__edit-patient-link"
              target="_blank"
              rel="noopener"
              :to="{ name: 'patients.overview.edit', params: { id: widgetModel.monitor.patient.id }, query: { division_id: widgetModel.monitor.division_id } }"
              @click.stop>
              <i class="mdi mdi-open-in-new"></i>
            </router-link>
          </span>
          <span class="monitoring-card__subtitle" v-if="hasSubtitle">
            <template v-if="subtitle">{{ subtitle }}</template>

            <slot v-else-if="$slots.subtitle" name="subtitle"></slot>
            <template v-else-if="widgetModel?.monitor">{{ widgetModel.monitor.division.name }} / {{ widgetModel.monitor.room.name }}</template>
          </span>
        </div>
      </div>

      <div class="header-actions">
        <template v-for="(action, index) in headerActionItems" >
          <div :key="index" v-if="action.icon && action._isVisible">
            <img v-if="action.icon.startsWith('/') || action.icon.startsWith('data:')" :src="action.icon" alt="">
            <i v-else :class="action.icon" :style="{ color: action.iconColor || '' }"></i>
          </div>
        </template>
      </div>


      <VDropdown class="monitoring-card__popup-button" container=".monitoring-card" v-if="showsPatientNotes && widgetModel && widgetModel.monitor" :triggers="['hover']" :popper-triggers="['hover']" >
        <i class="mdi mdi-tag-multiple-outline"></i>
          <template #popper>
            <div class="monitor-attributes-container">
              <div>
                <p class="box-title-inside">Patient Note</p>
                <div class="monitor-note monitor-attributes-content">{{ widgetModel?.monitor?.patient?.note || 'N/A' }}</div>
              </div>
              <div>
                <p class="box-title-inside">Patient Attributes</p>
                <div class="monitor-attributes monitor-attributes-content">
                  <template v-if="widgetModel?.monitor?.patient?.patient_tags?.length > 0">
                    <div class="tag is-link is-light" :key="index"
                         v-for="(attribute, index) in widgetModel.monitor.patient.patient_tags">
                      {{ attribute.tag.name }}
                    </div>
                  </template>
                  <p v-else>N/A</p>
                </div>
              </div>
              <div>
                <p class="box-title-inside">Patient Attention Score</p>
                <div class="monitor-note monitor-attributes-content">
                  <acuity-score :acuity-score-object="acuityScoreObject" is-inline></acuity-score>
                </div>
              </div>
            </div>
          </template>
      </VDropdown>

      <template v-if="$slots.headerUtils">
        <slot name="headerUtils"></slot>
      </template>

    </div>
    <div v-if="!withoutContent" class="monitoring-card__content" :class="{'monitoring-card__content--no-margin': !this.hasContentMargin}" ref="widget-container">
      <!-- @slot Content slot used if you want to display custom content inside the card instead of widget -->
      <slot v-if="$slots.content" name="content"></slot>
      <component v-else :is="widgetName + '-widget'" :widget-model="widgetModel" ref="widget-component"
                 @settings-updated="updateSettings"
                 @title-updated="v => cardTitle = v"
                 @subtitle-updated="v => cardSubtitle = v"
                 @color-updated="colorUpdated"
                 @size-changed="v => $emit('size-changed', v)"
                 @hide-toolbar="v => hideToolbar = v"
                 @popup-mode="v => $emit('popup-mode', v, widgetModel)"
                 @title-tooltip-updated="v => titleTooltip = v"
      ></component>
    </div>
    <div v-if="hasToolbar && !hideToolbar" class="monitoring-card__toolbar">
      <ul>
        <template v-if="widgetModel.monitor && widgetModel.monitor.status == 'active'">
          <li class="alarm-bell">
            <a href="#" @click.prevent="$emit('open-alarm', { monitorId: widgetModel.monitorId, isAlarm: true })">
              <i class="mdi mdi-bell"></i>
              <span>Alarm</span>
            </a>
          </li>
          <li>
            <a href="#" @click.prevent="$emit('open-alarm', { monitorId: widgetModel.monitorId, isAlarm: false })">
              <i class="mdi mdi-comment-alert"></i>
              <span>Event</span>
            </a>
          </li>
        </template>
        <li :class="{ 'has-badge': hasRoomMismatch }">
          <a href="#" @click.prevent="$emit('open-quickview', widgetModel.monitorId)">
            <i class="mdi mdi-page-next"></i>
            <span>Quick View</span>
          </a>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import PatientCardWidget from "@/components/widgets/PatientCardWidget";
import PatientCardWidgetSkeleton from "@/components/skeletons/PatientCardWidgetSkeleton";
import PatientEventsWidget from "@/components/widgets/PatientEventsWidget";
import PatientEventsWidgetSkeleton from "@/components/skeletons/PatientEventsWidgetSkeleton";
import BarChartWidget from "@/components/widgets/BarChartWidget";
import BarChartWidgetSkeleton from "@/components/skeletons/BarChartWidgetSkeleton";

import SpiderChartWidget from "@/components/widgets/SpiderChartWidget";


import ActigraphyChartWidget from "@/components/widgets/ActigraphyChartWidget";
import ActigraphyChartWidgetSkeleton from "@/components/skeletons/ActigraphyChartWidgetSkeleton";

import VideoWidget from "@/components/widgets/VideoWidget";
import LiveVideoWidget from "@/components/widgets/LiveVideoWidget";
import WebrtcVideoWidget from "@/components/widgets/WebrtcVideoWidget";
import WidgetModel from "@/models/WidgetModel";
import CardBadge from "@/components/card/CardBadge";
import {EventBus, EVENTS} from "@/lib/EventBus";
import {mapGetters, mapState} from "vuex";
import AcuityScore from "@/components/AcuityScore.vue";

/**
 * General use card component, used in safety center and patient monitoring page
 * @displayName Card
 */
export default {
  name: "Card",
  components: {
    AcuityScore,
    CardBadge,
    PatientCardWidget, PatientCardWidgetSkeleton,
    PatientEventsWidget, PatientEventsWidgetSkeleton,
    BarChartWidget, BarChartWidgetSkeleton,
    ActigraphyChartWidget, ActigraphyChartWidgetSkeleton,
    VideoWidget, LiveVideoWidget, SpiderChartWidget, WebrtcVideoWidget
  },
  props: {
    /**
     * Title of the card, displayed in the card header
     */
    title: {
      type: String
    },
    subtitle: {
      type: String
    },
    /**
     * Subtitle of the card, displayed right below the title
     */
    hasSubtitle: {
      type: Boolean,
      required: false,
      default: true,
    },
    /**
     * If true, three vertical dots will be displayed to the right end of the header
     * which will show available settings for the card in a dropdown menu.
     * Settings always contain link to delete widget, other settings/actions are provided from individual
     * widgets
     */
    hasSettings: {
      type: Boolean,
      required: false,
      default: false
    },
    /**
     * Indicator is small circle in the header which displays patient status color and on hover status
     * text in a tooltip
     */
    hasIndicator: {
      type: Boolean,
      required: false,
      default: false
    },
    /**
     * Toolbar displayed at the bottom, when hovering over the card.
     * At this moment toolbar items are fixed for all widgets
     */
    hasToolbar: {
      type: Boolean,
      required: false,
      default: false
    },
    /**
     * Whether or not to maintain aspect ratio of the card.
     * Setting turns on/off specified class on the root node of the card
     */
    maintainAspectRatio: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Setting turns on/off specified class on the root node of the card
     */
    autoHeight: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * WidgetModel is passed if we want to display specific widget in the card content
     * If not `content` slot can be used to load custom content. See Monitoring.vue
     */
    widgetModel: {
      type: WidgetModel,
      required: false
    },
    /**
     * Setting this to true will add bottom margin to content so it's visible when toolbar overlays bottom part
     */
    compensateToolbarHeight: {
      type: Boolean,
      required: false,
      default: false
    },
    /**
     * Whether card content has margins
     */
    hasContentMargin: {
      type: Boolean,
      required: false,
      default: true
    },
    showsPatientNotes: {
      type: Boolean,
      required: false,
      default: false
    },
    /**
     * Show historical/paused badge at the top of the card
     */
    showBadge: {
      type: Boolean,
      required: false,
      default: false
    },
    divisionColor: {
      type: String,
      required: false,
      default: ''
    },
    withoutContent: {
      type: Boolean,
      required: false,
      default: false
    },
    autoHeaderHeight: {
      type: Boolean,
      required: false,
      default: false
    },
    canChangeBlockType: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  methods: {
    /**
     * Callback for 'update-settings' event that's emitted from widget when new actions/setting items are provided
     * @param settings
     */
    updateSettings(settings) {
      let componentSettings = [...settings]
      this.headerActionItems = []
      componentSettings.forEach((item, index) => {
        if (item.type == 'header') {
          this.headerActionItems.push(item)
          componentSettings.splice(index, 1)
        }
      })
      this.componentSettings = componentSettings
    },
    mouseOver() {
      EventBus.$emit(EVENTS.CARD_HOVER, this.widgetModel)
    },
    colorUpdated(color, isAnimated) {
      this.nudgeColor = color
      this.isAnimatedNudge = isAnimated
    }
  },
  computed: {
    ...mapGetters('AcuityScoresStore', ['getAcuityScore']),
    acuityScoreObject() {
      if (!this.widgetModel.monitorId && !this.widgetModel.monitor) {
        return null
      }
      return this.getAcuityScore(this.widgetModel?.monitor?.id || this.widgetModel.monitorId)
    },
    widgetName() {
      if (this.widgetModel.name == 'live-video') {
        return 'Webrtc-Video'
      } else {
        return this.widgetModel.name
      }
    },
    cSubtitle() {
      return this.subtitle || this.cardSubtitle
    },
    canEditPatient() {
      return this.$can('Edit', 'Patient')
    },
    isWebrtcVideoWidget() {
      return this.widgetModel?.name === 'live-video'
    },
    hasTitle() {
      return this.title || this.cardTitle
    },
    ...mapState('CentralMonitoringStore', ['roomMismatchMonitors']),
    hasRoomMismatch() {
      return this.roomMismatchMonitors.has(this.widgetModel.monitorId)
    },
  },
  mounted() {
    if (this.$refs.monitoringCard &&
      this.widgetModel?.settings?.isCentralMonitoring && ['video', 'live-video'].includes(this.widgetModel?.name)) {
      this.$refs.monitoringCard.addEventListener('mouseenter', this.mouseOver)
    }
  },
  unmounted() {
    this.$refs?.monitoringCard?.removeEventListener('mouseenter', this.mouseOver)
  },
  data() {
    return {
      cardTitle: null,
      cardSubtitle: null,
      componentSettings: [],
      headerActionItems: [],
      nudgeColor: null,
      isAnimatedNudge: true,
      hideToolbar: false,
      cardClasses: {
        'monitoring-card--aspect-ratio': this.maintainAspectRatio,
        'monitoring-card--auto-height': this.autoHeight,
        'monitoring-card--toolbar-compensation': this.compensateToolbarHeight
      },
      titleTooltip: null
    }
  }
}
</script>

<style lang="scss" scoped>
  .has-animated-nudge {
    animation: blink 1s normal infinite;
  }

  @keyframes blink {
    0% {
      outline-width: 4px;
    }
    50% {
      outline-width: 0;
    }
    100% {
      outline-width: 5px;
    }
  }
  .monitoring-card {
    background: var(--ld-bg-secondary);
    border-radius: 4px;
    @include main-shadow();
    display: flex;
    flex-direction: column;
    &:hover > .monitoring-card__toolbar {
      opacity: 1;
    }
    outline: 5px solid transparent;
  }
  .monitoring-card.monitoring-card--toolbar-compensation {
    &:hover > .monitoring-card__content {
      margin-bottom: 40px;
    }
  }
  .monitoring-card__content {
    flex: 1;
    display: flex;
    align-items: center;
    position: relative;
    overflow-y: auto;
    :slotted(> div),
    > div {
      flex: 1;
      position: absolute;
      top: 0;
      left: 0px;
      height: calc(100% - 30px);
      width: calc(100% - 30px);
      margin: 15px
    }
    &.monitoring-card__content--no-margin {
      :slotted(> div),
      > div {
        width: 100%;
        height: 100%;
        margin: 0;
      }
    }
  }
  .monitoring-card.monitoring-card--auto-height {
    .monitoring-card__content {
      margin: 0;
      :slotted(> div),
      > div {
        position: static;
        overflow-y: initial;
        //max-height: 300px;
      }
    }
  }
  .monitoring-card.monitoring-card--aspect-ratio {
    .monitoring-card__content {
      position: relative;
      padding-top: calc(56.25% + 1px);
      :slotted(> div),
      > div {
        position: absolute;
        top: 0;
        left: 0;
      }
    }
  }
  .monitoring-card__header {
    width: 100%;
    height: 47px;
    border-radius: 4px 4px 0 0;
    display: flex;
    align-items: center;
    padding: 4px 15px 4px 0;
    border-bottom: 1px solid var(--ld-border-default);
    .auto-header-height & {
      height: auto;
    }
  }

  .monitoring-card__title-tooltip {
    display: flex;
    flex-direction: column;
  }
  .monitoring-card__title-container {
    display: flex;
    flex-direction: column;
    flex: 1;
    justify-content: space-around;
    height: 100%;
    margin-left: 10px;
    line-break: anywhere;
    > span {
      display: flex;
      flex-direction: column;
      &:after {
        white-space: pre;
        font-size: 13px;
        line-height: 16px;
      }
    }
  }
  .monitoring-card__subtitle {
    font-size: 13px;
    opacity: 0.8;
    overflow-wrap: anywhere;
  }
  .monitoring-card__title {
    font-size: 12px;
    text-transform: uppercase;
    font-weight: 500;
    letter-spacing: 1.1px;
  }
  .monitoring-card__edit-patient-link {
    margin-left: 6px;
    color: inherit;
    opacity: 0.7;
    &:hover {
      opacity: 1;
    }
  }
  .monitoring-card__settings {
    margin-left: auto;
    cursor: pointer;
    padding: 0 0 0 5px;
    :deep(.dropdown-content) {
      box-shadow: 0 0.5em 1em -0.125em rgb(10 10 10 / 37%), 0 0px 0 1px rgb(10 10 10 / 8%);
      max-width: 200px;
    }
  }
  .monitoring-card__settings :deep(.dropdown-menu) {
    z-index: 100;
  }
  .monitoring-card__toolbar {
    width: calc(100% + 20px);
    height: 40px;
    background: var(--ld-bg-secondary);
    position: absolute;
    bottom: -10px;
    opacity: 0;
    left: -10px;
    border-radius: 7px;
    transition: opacity 0.12s linear;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
    display: flex;
    z-index: 99;
    flex: 1;
    align-items: center;
    ul {
      flex: 1;
      display: flex;
      justify-content: space-around;
      li {
        //max-width: 30px;
        overflow: hidden;
        transition: all 0.1s linear;
        border-radius: 5px;
        padding: 5px 7px 4px;
        &:hover {
          background: var(--ld-bg-primary);
          transition: all 0.1s linear;
          a {
            color: var(--ld-brand-primary);
          }
        }
      }
      a {
        font-size: 16px;
        display: flex;
        align-items: center;
        white-space: nowrap;
        span {
          font-size: 13px;
          margin-left: 5px;
          line-height: 1;
        }
      }
    }
  }
  .monitoring-card__header {
    position: relative;
  }
  .is-section {
    font-size: 10px;
    text-transform: uppercase;
    font-weight: 600;
    letter-spacing: 1.3px;
    opacity: 0.8;
  }
  .settings-buttons {
    overflow: hidden;
    text-overflow: ellipsis;
    padding-right: 1.3rem;
    display: flex;
    align-items: center;
    > i {
      padding-right: 5px;
    }
    &.is-danger {
      color: var(--ld-status-danger);
    }
    &.is-warning {
      color: var(--ld-status-warning);
    }
    &.is-success {
      color: var(--ld-status-success);
    }
  }
  .settings-buttons :deep(.control) {
    flex: 1 1;
  }
  .settings-buttons :deep(.b-radio.radio.is-small) {
    border-radius: 6px;
  }
  :deep(.monitoring-card__popup-button) {
    font-size: 14px;
    cursor: pointer;
    margin-left: 10px;
  }
  .monitor-attributes-container {
    max-width: 300px;
    max-height: 195px;
    font-size: 14px;
    color: var(--ld-text-primary);
    padding-bottom: 5px;
    .box-title-inside {
      font-size: 11px;
      margin-bottom: 0;
      font-weight: 500;
      letter-spacing: 1.8px;
      padding: 6px 10px 5px;
      background: var(--ld-brand-primary);
      color: var(--ld-text-inverse);
    }
  }
  .monitor-attributes-content {
    padding: 7px 10px;
  }
  .monitor-note {
    line-height: 16px;
  }
  .monitor-attributes {
    white-space: pre-wrap;
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
  }
  .alarm-bell a {
    color: var(--ld-status-danger);
  }
  .division-color {
    width: 6px;
    height: 100%;
    position: absolute;
    left: 0;
    top: 0;
    border-radius: 4px 0 0 0;
  }
  .header-actions {
    margin: 0 5px;
    img {
      width: 17px;
    }
  }
  .monitoring-card.without-content {
    .monitoring-card__header {
      border: none;
    }
    .division-color {
      border-radius: 4px 0 0 4px;
    }
  }
  .monitoring-card.without-settings {
    .monitoring-card__title-container {
      margin-left: 15px;
    }
  }
  .dropdown-divider {
    margin: 0.2rem 0;
  }
  .monitoring-card__toolbar .has-badge {
    position: relative;
    &:after {
      content: '';
      position: absolute;
      top: 0;
      right: 1px;
      width: 8px;
      height: 8px;
      background: var(--ld-status-danger);
      border-radius: 50%;
    }
  }
</style>
